import {
  reactive, getCurrentInstance, watch, toRefs,
} from '@vue/composition-api'
import {
  flatten,
  omit,
  sortBy,
  // sortedUniqBy,
} from 'lodash'
import dayjs from 'dayjs'
import router from '@/router'
import i18n from '@/libs/i18n'
// eslint-disable-next-line object-curly-newline

export const isObject = obj => typeof obj === 'object' && obj !== null

export const NEW_ORDER_DRAFT_KEY = 'ok-menu:new-order-draft'
export const NEW_ORDER_DRAFT_VERSION = 1

export const isToday = date => {
  const today = new Date()
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  )
}

const getRandomFromArray = array => array[Math.floor(Math.random() * array.length)]

// ? Light and Dark variant is not included
// prettier-ignore
export const getRandomBsVariant = () => getRandomFromArray(['primary', 'secondary', 'success', 'warning', 'danger', 'info'])

export const isDynamicRouteActive = route => {
  const { route: resolvedRoute } = router.resolve(route)
  return resolvedRoute.path === router.currentRoute.path
}

// Thanks: https://medium.com/better-programming/reactive-vue-routes-with-the-composition-api-18c1abd878d1
export const useRouter = () => {
  const vm = getCurrentInstance().proxy
  const state = reactive({
    route: vm.$route,
  })

  watch(
    () => vm.$route,
    r => {
      state.route = r
    },
  )

  return { ...toRefs(state), router: vm.$router }
}

export const getIntervalInMinutes = ({ interval = 0, scale = 'min' } = {}) => {
  const scales = {
    min: 1,
    hour: 60,
    day: 60 * 24,
  }
  return interval * scales[scale]
}

export const filterSlotsByMinAdvance = (
  slots,
  chosenDate = dayjs().format('YYYY/MM/DD'),
  applyOnEnd = false,
  minDateTimeOverride = null,
  hour = null,
  currentSellingTypeConfig,
) => {
  const scheduledMinAdvance = getIntervalInMinutes(currentSellingTypeConfig?.scheduled_orders?.min_advance) ?? 0

  let compareTime
  if (hour) {
    compareTime = dayjs(`${chosenDate} ${hour}`, 'YYYY/MM/DD HH:mm:ss')
  } else {
    const minDateTime = minDateTimeOverride || dayjs().add(scheduledMinAdvance + 15, 'minute')
    compareTime = dayjs(minDateTime)
  }
  return slots.filter(slot => {
    const timeToCompare = applyOnEnd ? slot.to : slot.from
    const fullSlotTime = dayjs(`${chosenDate} ${timeToCompare}`, 'YYYY-MM-DD hh:mm A')
    return compareTime.isBefore(fullSlotTime, 'minute')
  })
}

export const calculateReadyDate = (preparation, nowDate = new Date(), isFullDate = false) => {
  if (!preparation || !preparation.minutes) return null

  const readyDateObj = new Date(nowDate.getTime() + preparation.minutes * 60000)

  const year = readyDateObj.getFullYear()
  const month = String(readyDateObj.getMonth() + 1).padStart(2, '0')
  const day = String(readyDateObj.getDate()).padStart(2, '0')

  const hours = String(readyDateObj.getHours()).padStart(2, '0')
  const minutes = String(readyDateObj.getMinutes()).padStart(2, '0')
  if (isFullDate) return `${year}/${month}/${day} ${hours}:${minutes}`
  console.log('------------ year -------------', year)
  console.log('------------ month -------------', month)
  console.log('------------ day -------------', day)
  return `${year}/${month}/${day}`
}

export const getMaxPreparationTime = orderData => {
  const orderlines = orderData.order_lines
  if (!orderlines.length) return null

  let maxItem = null
  let maxMinutes = -1
  // eslint-disable-next-line no-restricted-syntax
  for (const orderline of orderlines) {
    const label = orderline?.price_label_object
    // eslint-disable-next-line no-continue
    if (!label) continue

    const type = label.preparation_time_type
    const value = Number(label.preparation_time)
    // eslint-disable-next-line no-restricted-globals,no-continue
    if (!type || isNaN(value)) continue

    // eslint-disable-next-line no-nested-ternary
    const minutes = type === 'm' ? value : type === 'h' ? value * 60 : type === 'd' ? value * 1440 : null

    // eslint-disable-next-line no-continue
    if (minutes === null) continue

    if (minutes > maxMinutes) {
      maxMinutes = minutes
      maxItem = { type, value, minutes }
    }
  }

  return maxItem
}

export function formatAmPm(time) {
  const { locale } = i18n
  if (!time) return ''
  if (locale === 'ar') {
    return time.replace('AM', i18n.t('orders.am_label')).replace('PM', i18n.t('orders.pm_label'))
  }
  return time
}

export function getAllAreas(zones) {
  let areas = zones.reduce(
    (prev, zone) => prev.concat(
      zone.areas
        .filter(area => area.name && area.name !== 'undefined | undefined')
        .map(area => ({
          ...area,
          osm_id: area.osm_id,
          fees: zone.fees,
          governorate: !area.parent_name,
          parent_name: area.parent_name || area.name,
          branch: zone.branch,
          zone_id: zone.id,
          zone: omit(zone, ['areas', 'shape', 'visible', 'name', 'color']),
        })),
    ),
    [],
  )
  areas = flatten(areas)
  areas = sortBy(areas, ['osm_id', 'fees'])
  // areas = sortedUniqBy(areas, 'osm_id')
  areas = sortBy(areas, ['governorate', 'name'])
  return areas
}

export const getAreaName = (name, area = {}, areas = []) => {
  const { locale } = i18n
  const areaNames = name?.split(' | ')
  const areaName = (locale === 'ar' ? areaNames?.[0] : areaNames?.[1]) ?? name
  if (area?.governorate) {
    return i18n.t(areas.length === 1 ? 'delivery_labels.area_inside' : 'delivery_labels.other_area_inside', [areaName])
  }
  return areaName
}
/**
 * This is just enhancement over Object.extend [Gives deep extend]
 * @param {target} a Object which contains values to be overridden
 * @param {source} b Object which contains values to override
 */
// export const objectExtend = (a, b) => {
//   // Don't touch 'null' or 'undefined' objects.
//   if (a == null || b == null) {
//     return a
//   }

//   Object.keys(b).forEach(key => {
//     if (Object.prototype.toString.call(b[key]) === '[object Object]') {
//       if (Object.prototype.toString.call(a[key]) !== '[object Object]') {
//         // eslint-disable-next-line no-param-reassign
//         a[key] = b[key]
//       } else {
//         // eslint-disable-next-line no-param-reassign
//         a[key] = objectExtend(a[key], b[key])
//       }
//     } else {
//       // eslint-disable-next-line no-param-reassign
//       a[key] = b[key]
//     }
//   })

//   return a
// }
